import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children }) => {
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setVisible(true);
  };

  const hideTooltip = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
    }, 100); // Delay to prevent flickering
  };

  const updateTooltipPosition = () => {
    const triggerElement = triggerRef.current;
    const tooltipElement = tooltipRef.current;
    if (triggerElement && tooltipElement) {
      const triggerRect = triggerElement.getBoundingClientRect();
      const tooltipRect = tooltipElement.getBoundingClientRect();

      // Calculate initial position above the trigger
      let top = triggerRect.top - tooltipRect.height - 8; // 8px offset
      let left =
        triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      // Check for horizontal overflow and adjust
      if (left < 8) {
        left = 8; // 8px from the left edge
      } else if (left + tooltipRect.width > window.innerWidth - 8) {
        left = window.innerWidth - tooltipRect.width - 8; // 8px from the right edge
      }

      // Check for vertical overflow and flip if needed
      if (top < 8) {
        top = triggerRect.bottom + 8; // Position below the trigger
      }

      setTooltipStyle({
        position: "absolute",
        top: `${top + window.scrollY}px`,
        left: `${left + window.scrollX}px`,
        zIndex: 1000,
      });
    }
  };

  useEffect(() => {
    if (visible) {
      updateTooltipPosition();
      window.addEventListener("resize", updateTooltipPosition);
      window.addEventListener("scroll", updateTooltipPosition);
    }
    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition);
    };
  }, [visible]);

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </div>
      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={tooltipStyle}
            className="bg-white dark:bg-gray-800 text-black dark:text-white text-sm p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-w-md"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
